import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTwoFactorService } from '../../../../use-cases/authentication/useTwoFactorService';//'../../../use-cases/authentication/useTwoFactorService';
import { useAppSelector } from '../../../../application/states/hooks';
import { CodeInput } from './components/CodeInput';
import { BackupCodeInput } from './components/BackupCodeInput';
import { executeAlert } from '../../../../application/states/toast-alert/toast-alert';

// ✅ STYLED COMPONENTS
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 16px;
  margin: 0 0 32px 0;
`;

const StepContainer = styled.div`
  margin-bottom: 24px;
`;

const StepTitle = styled.h2`
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const StepDescription = styled.p`
  color: #7f8c8d;
  font-size: 14px;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const QRCodeContainer = styled.div`
  background: #f8f9fa;
  border: 2px dashed #e1e5e9;
  border-radius: 12px;
  padding: 32px;
  margin: 24px 0;
`;

const QRCodeImage = styled.img`
  max-width: 200px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SecretText = styled.div`
  background: #f1f3f4;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px 0;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #2c3e50;
  word-break: break-all;
`;

const BackupCodesContainer = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const BackupCodesTitle = styled.h4`
  color: #856404;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const BackupCodesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const BackupCode = styled.span`
  background: white;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 4px 8px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #856404;
  text-align: center;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#3498db' : '#f8f9fa'};
  color: ${props => props.variant === 'primary' ? 'white' : '#2c3e50'};
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid #e1e5e9'};
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin: 8px;
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'primary' ? '#2980b9' : '#e9ecef'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #7f8c8d;
  font-size: 16px;
  
  &:before {
    content: "⏳";
    margin-right: 12px;
    animation: spin 1s linear infinite;
    font-size: 20px;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const TwoFactorPage: React.FC = () => {
  const [step, setStep] = useState<'qr' | 'verify'>('qr');
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { activateTwoFactor, verifyTwoFactor } = useTwoFactorService();
  const user = useAppSelector((state) => state.user);
  useEffect(() => {
    handleActivateTwoFactor();
  }, []);

// TwoFactorPage.tsx - linha 175
// TwoFactorPage.tsx - linha 175
// TwoFactorPage.tsx - linha 175
const handleActivateTwoFactor = async () => {
  try {
    setLoading(true);
    
    // ✅ VERIFICAR se 2FA já foi ativado:
    const existingSecret = localStorage.getItem('TWO_FACTOR_SECRET');
    const existingQrCode = localStorage.getItem('TWO_FACTOR_QR_CODE');
    const existingBackupCodes = localStorage.getItem('TWO_FACTOR_BACKUP_CODES');
    
    if (existingSecret && existingQrCode && existingBackupCodes) {
      
      // ✅ USAR dados existentes:
      setSecret(existingSecret);
      setQrCodeUrl(existingQrCode);
      setBackupCodes(JSON.parse(existingBackupCodes));
      
      // ✅ CORRIGIR: começar no passo 1 (QR Code):
      setStep('qr'); // ✅ MOSTRAR QR Code primeiro!
      
      // ✅ LIMPAR localStorage após uso:
      localStorage.removeItem('TWO_FACTOR_SECRET');
      localStorage.removeItem('TWO_FACTOR_QR_CODE');
      localStorage.removeItem('TWO_FACTOR_BACKUP_CODES');
      localStorage.removeItem('TWO_FACTOR_USER_ID');
      
      return;
    }
    
    // ✅ SE não tiver dados, continuar com API:
    let userId = user.id || null;
    
    if (!userId) {
      userId = localStorage.getItem('TWO_FACTOR_USER_ID');
    }
    
    if (!userId) {
      console.error('❌ userId não encontrado nem no Redux nem no localStorage');
      throw new Error('ID do usuário não encontrado');
    }
    
    
    const result = await activateTwoFactor(userId);
    
    setSecret(result.secret);
    setQrCodeUrl(result.qrCodeUrl);
    setBackupCodes(result.backupCodes);
    setStep('verify');
    
  } catch (error) {
    console.error('Erro ao ativar 2FA:', error);
  } finally {
    setLoading(false);
  }
};
  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      const result = await verifyTwoFactor(verificationCode);
      
      if (result.success) {
        // 2FA ativado com sucesso
        window.location.href = '/workspace';
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && step === 'qr') {
    return (
      <Container>
        <Card>
          <LoadingSpinner>
            Ativando 2FA...
          </LoadingSpinner>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Autenticação de Dois Fatores</Title>
        <Subtitle>Configure seu 2FA para maior segurança</Subtitle>

        {step === 'qr' && (
          <StepContainer>
            <StepTitle>Passo 1: Escaneie o QR Code</StepTitle>
            <StepDescription>
              Use seu aplicativo authenticator (Google Authenticator) 
              para escanear o QR Code abaixo
            </StepDescription>
            
            <QRCodeContainer>
              <QRCodeImage src={qrCodeUrl} alt="QR Code 2FA" />
            </QRCodeContainer>
            
            <StepTitle>Chave Secreta</StepTitle>
            <StepDescription>
              Se não conseguir escanear, digite manualmente esta chave:
            </StepDescription>
            <SecretText>{secret}</SecretText>
            
            <BackupCodesContainer>
              <BackupCodesTitle>⚠️ Códigos de Backup</BackupCodesTitle>
              <StepDescription>
                Guarde estes códigos em local seguro. Use-os se perder acesso ao authenticator.
              </StepDescription>
              <BackupCodesList>
                {backupCodes.map((code, index) => (
                  <BackupCode key={index}>{code}</BackupCode>
                ))}
              </BackupCodesList>
            </BackupCodesContainer>
            
            <Button variant="primary" onClick={() => setStep('verify')}>
              Próximo: Verificar Código
            </Button>
          </StepContainer>
        )}

        {step === 'verify' && (
          <StepContainer>
            <StepTitle>Passo 2: Verifique seu Código</StepTitle>
            <StepDescription>
              Digite o código de 6 dígitos gerado pelo seu authenticator
            </StepDescription>
            
            <CodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              onVerify={handleVerifyCode}
              loading={loading}
            />
            
            <Button 
              variant="secondary" 
              onClick={() => setStep('qr')}
              disabled={loading}
            >
              Voltar
            </Button>
          </StepContainer>
        )}
      </Card>
    </Container>
  );
};